import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDeleteGoals, useGetGoals } from '../../../../../api/goals'
import Loading from '../../../../molecules/Loading'
import EmptySection from '../../../../atoms/EmptySection'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import ConfirmationModal from '../../../../molecules/ConfirmationModal'
import { useCheckPolicy } from '../../../../../hooks'
import { PERMISSIONS } from '../../../../../policies/admin'
import GoalModal from './goalModal'

const FIRM_LEVEL_TYPE_ID = 1000000
const FirmGoals = () => {
  const canViewFirmGoals = useCheckPolicy(PERMISSIONS.viewFirmGoals)
  const canEditFirmGoals = useCheckPolicy(PERMISSIONS.editFirmGoals)
  const { data: firmGoals, isLoading, error } = useGetGoals(FIRM_LEVEL_TYPE_ID, { enabled: canViewFirmGoals })
  const [goalSchema, setGoalSchema] = React.useState(null)

  const defaultColumnConfig = useMemo(() => ({
    columns: [
      { Header: 'Code', accessor: 'code', type: 'string' },
      { Header: 'Name', accessor: 'name', type: 'string' },
      { Header: 'Description', accessor: 'description', type: 'string' },
      { Header: 'Value', accessor: 'value', type: 'number' },
      { Header: 'Unit', accessor: 'unit', type: 'string' },
      { Header: 'Start Date', accessor: 'startDate', type: 'date' },
      { Header: 'End Date', accessor: 'endDate', type: 'date' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => ActionsCell({
          canEditContent: canEditFirmGoals,
          canDelete: canEditFirmGoals,
          editFunction: () => {
            setGoalSchema({ ...row.original, levelTypeId: FIRM_LEVEL_TYPE_ID })
            setGoalModalOpen(true)
          },
          deleteFunction: () => {
            setDeleteError(null)
            setDeleteModalOpen(true)
            setDeletedId(row.original.goalId)
          }
        })
      }
    ],
    defaultSort: [{ id: 'code', desc: false }]
  }), [canEditFirmGoals])
  const { mutateAsync, isLoading: deleteLoading } = useDeleteGoals()
  const [deletedId, setDeletedId] = React.useState(null)
  const [deleteError, setDeleteError] = React.useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [goalModalOpen, setGoalModalOpen] = React.useState(false)
  const onAddClick = () => {
    setGoalSchema({
      code: '',
      name: '',
      description: null,
      value: null,
      unit: null,
      startDate: null,
      endDate: null,
      levelTypeId: FIRM_LEVEL_TYPE_ID
    })
    setGoalModalOpen(true)
  }

  if (!canViewFirmGoals) {
    return (
      <EmptySection
        title='Access Denied'
        description='You do not have sufficient permissions to access this resource'
      />
    )
  }

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return <div> Error loading firm goals: {error.message}</div>
  }
  return (
    <>
      <ConfirmationModal
        open={deleteModalOpen || deleteLoading}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={async () => {
          const result = await mutateAsync({ deletedId, levelTypeId: FIRM_LEVEL_TYPE_ID })
          if (result.success) {
            setDeletedId(null)
            setDeleteModalOpen(false)
          } else {
            setDeleteModalOpen(false)
            setDeleteError(result?.message || 'Failed to delete the goal. Please try again.')
          }
        }}
        isLoading={deleteLoading}
        confirmationType='delete'
        confirmationText='Are you sure you want to delete?'
        buttonLabelCancel='Cancel'
        buttonLabelConfirm='Delete'
      />
      <div className='flex flex-row justify-end mb-4'>
        <SydButton size='md' variant='primary' icon='add' onClick={() => onAddClick()}>Add Firm Goal</SydButton>
      </div>
      <GoalModal
        open={goalModalOpen}
        onClose={() => setGoalModalOpen(false)}
        goalSchema={goalSchema}
      />
      {deleteError && <div style={{ color: 'red', marginBottom: '1rem' }}>{deleteError}</div>}

      {firmGoals?.length > 0 ? (
        <OperationalTable
          variant='v2'
          mode='auto'
          itemName='FirmGoals'
          columns={defaultColumnConfig.columns}
          defaultSort={defaultColumnConfig.defaultSort}
          data={firmGoals}
          total={firmGoals.length}
        />
      ) : (
        <EmptySection
          title='No Goals'
          description='No firm goals have been added yet. Use the button above to add one.'
        />
      )}

    </>
  )
}

export default FirmGoals

export const ActionsCell = ({ canDelete, deleteFunction, editFunction, canEditContent }) => {
  return (
    <div className='flex flex-row items-center'>
      <div className='flex flex-row gap-2 pr-4'>
        <SydButton size='sm' variant='outline' icon='edit' onClick={() => editFunction()} disabled={!canEditContent}>Edit</SydButton>
        <SydButton size='sm' variant='outline' priority='warning' icon='delete' onClick={deleteFunction} disabled={!canDelete || !canEditContent}>Delete</SydButton>
      </div>
    </div>
  )
}

ActionsCell.propTypes = {
  canDelete: PropTypes.bool,
  editFunction: PropTypes.func,
  deleteFunction: PropTypes.func,
  canEditContent: PropTypes.bool
}
